import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, X } from 'lucide-react';
import {
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@cargomatic/ui';
import EditButton from 'components/v2/buttons/EditButton';
import Spinner from 'components/ui/Spinner';
import SessionStore from 'stores/SessionStore';
import { getStopDocumentsConfig } from 'utils/UserServiceAPIUtils';
import EditShipperRulesModal from './EditStopDocumentsModal';

const MOVEMENT_TYPES = [
  'IMPORT_DRAYAGE',
  'IMPORT_RAIL',
  'EXPORT_DRAYAGE',
  'EXPORT_RAIL',
  'REPOSITION_DRAYAGE',
  'REPOSITION_RAIL',
  'DOMESTIC_DRAYAGE',
  'DOMESTIC_RAIL',
];

const STOP_TYPES = [
  'LOADED_PICKUP',
  'LOADED_DELIVERY',
  'STOP_EMPTY_PICKUP',
  'STOP_EMPTY_DELIVERY',
  'OFFSITE_LOADED_PICKUP',
  'OFFSITE_LOADED_DELIVERY',
  'OFFSITE_EMPTY_PICKUP',
  'OFFSITE_EMPTY_DELIVERY',
];

function StopTypeCard({ stopType, onClickEdit, canEdit }) {
  const { t } = useTranslation();
  const hasDocs = stopType.docTypes.length > 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-gray-700" aria-hidden="true" />
          <p className="text-base font-semibold text-gray-900">
            {t(stopType.name?.toUpperCase())}
          </p>
        </div>
        {canEdit ? (
          <EditButton
            size="small"
            onClick={() => onClickEdit(stopType)}
            dataTestId={`editStopTypeButton-${stopType.name}`}
          />
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <EditButton
                    size="small"
                    disabled
                    dataTestId={`editStopTypeButton-${stopType.name}`}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">
                You need the shipper-onboarding role to edit document
                requirements.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {hasDocs ? (
        <div className="mt-3">
          <p className="text-sm text-gray-500">Required Documents:</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {stopType.docTypes.map((docType) => (
              <Badge
                key={docType}
                variant="secondary"
                className="gap-1.5 rounded-full px-3 py-1 font-medium"
              >
                {t(docType?.toUpperCase())}
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </Badge>
            ))}
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm italic text-gray-500">
          No specific documents required
        </p>
      )}
    </div>
  );
}

export default function StopDocumentRequirements({ id }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [userStopConfigs, setUserStopConfigs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentMovement, setCurrentMovement] = useState('');
  const [currentStopType, setCurrentStopType] = useState({});
  const [selectedMovementType, setSelectedMovementType] = useState(
    MOVEMENT_TYPES[0],
  );
  const canEditSettings = SessionStore.hasShipperOnboardingRole();

  function renderSpinner() {
    return <Spinner showMessage message={t('LOADING')} />;
  }

  function toggleModal() {
    setShowModal(!showModal);
  }

  function handleClickEdit(movementType, stopType) {
    setCurrentMovement(movementType);
    setCurrentStopType(stopType);
    toggleModal();
  }

  function getDocumentRequirements(stopConfigs, movementType, stopType) {
    const [movement, shipmentType] = movementType.split('_');
    const offsite = stopType.includes('OFFSITE');
    const empty = stopType.includes('EMPTY');
    const actionType = stopType.includes('PICKUP') ? 'PICKUP' : 'DELIVER';
    const foundConfig = stopConfigs?.find(
      (config) =>
        config.movementType === movement &&
        config.shipmentType === shipmentType &&
        config.offsite === offsite &&
        config.empty === empty &&
        config.actionType === actionType,
    );

    const docs = foundConfig?.stopDocuments;
    return docs != null ? docs : [];
  }

  function transformData() {
    const mappedGroups = MOVEMENT_TYPES.map((movementType) => {
      const group = {
        movementType,
      };

      group.stopTypes = STOP_TYPES.map((stopType) => {
        return {
          name: stopType,
          docTypes: getDocumentRequirements(
            userStopConfigs,
            movementType,
            stopType,
          ),
        };
      });

      return group;
    });

    return mappedGroups;
  }

  const fetchGroupedStopDocuments = async (id) => {
    try {
      const userResponse = await getStopDocumentsConfig(id);
      if (!userResponse) {
        throw new Error('No response from API');
      }
      const configs =
        userResponse?.data?.getStopDocumentsConfigQuery?.stopConfigurations ??
        [];
      setUserStopConfigs(configs);
    } catch (err) {
      console.error('Error during getStopDocumentsConfig: ', err.message);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchGroupedStopDocuments(id);
  }, []);

  useEffect(() => {
    setGroups(transformData());
    setIsLoading(false);
  }, [userStopConfigs]);

  useEffect(() => {
    if (!showModal) {
      fetchGroupedStopDocuments(id);
    }
  }, [showModal]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.movementType === selectedMovementType),
    [groups, selectedMovementType],
  );

  if (isLoading) return renderSpinner();

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center gap-3">
        <Label
          htmlFor="movement-type-select"
          className="text-sm font-medium text-gray-700"
        >
          Order Type:
        </Label>
        <Select
          value={selectedMovementType}
          onValueChange={setSelectedMovementType}
        >
          <SelectTrigger
            id="movement-type-select"
            className="w-64"
            data-testid="movement-type-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MOVEMENT_TYPES.map((mt) => (
              <SelectItem key={mt} value={mt}>
                {t(mt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedGroup && (
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-xl font-bold text-gray-900">
            {t(selectedGroup.movementType)}
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {selectedGroup.stopTypes.map((stopType) => (
              <StopTypeCard
                key={stopType.name}
                stopType={stopType}
                canEdit={canEditSettings}
                onClickEdit={(st) =>
                  handleClickEdit(selectedGroup.movementType, st)
                }
              />
            ))}
          </div>
        </section>
      )}

      {showModal && (
        <EditShipperRulesModal
          currentMovement={currentMovement}
          currentStop={currentStopType}
          onHide={toggleModal}
          id={id}
        />
      )}
    </div>
  );
}
